/**
 * InputSummary - Transparency Component
 *
 * Shows users exactly what data drove their journey recommendations.
 * Builds trust by being explicit about inputs, assumptions, and methodology.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { FactPack } from '@/lib/ai/schemas/factPack.schema';

interface InputSummaryProps {
  factPack: FactPack;
  className?: string;
}

// Format currency helpers
function formatUSD(amount: number): string {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount.toLocaleString()}`;
}

function formatINR(amount: number): string {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  return `₹${amount.toLocaleString('en-IN')}`;
}

// Input row component
function InputRow({
  label,
  value,
  source,
  highlight
}: {
  label: string;
  value: string | number;
  source?: 'user' | 'calculated' | 'default';
  highlight?: boolean;
}) {
  const sourceColors = {
    user: 'bg-emerald-100 text-emerald-700',
    calculated: 'bg-blue-100 text-blue-700',
    default: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className={cn(
      "flex items-center justify-between py-2 border-b border-slate-100 last:border-0",
      highlight && "bg-primary-50/50 -mx-3 px-3 rounded-lg"
    )}>
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-600">{label}</span>
        {source && (
          <span className={cn(
            "text-[9px] px-1.5 py-0.5 rounded-full uppercase tracking-wide font-medium",
            sourceColors[source]
          )}>
            {source === 'user' ? 'Your Input' : source === 'calculated' ? 'Calculated' : 'Default'}
          </span>
        )}
      </div>
      <span className="text-sm font-medium text-slate-800 font-mono">{value}</span>
    </div>
  );
}

// Section header
function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3 pt-2">
      <span className="text-base">{icon}</span>
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</h4>
    </div>
  );
}

export function InputSummary({ factPack, className }: InputSummaryProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract key data from factPack
  const { profile, fireSnapshot, assets, taxStrategy, dataQuality } = factPack;

  // Calculate totals for display
  const usAssetsTotal = assets.residentCountry.total;
  const indiaAssetsTotal = assets.india.total;

  return (
    <div className={cn("bg-slate-50/80 rounded-2xl border border-slate-200", className)}>
      {/* Collapsed Header - Always Visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-100/50 transition-colors rounded-2xl"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
            <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Your Inputs & Assumptions</h3>
            <p className="text-xs text-slate-500">See exactly what drove these recommendations</p>
          </div>
        </div>
        <motion.svg
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-5 h-5 text-slate-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </motion.svg>
      </button>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 space-y-4">
              {/* Quick Summary Bar */}
              <div className="bg-white rounded-xl p-4 border border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-primary-600">{profile.currentCountry}</div>
                  <div className="text-[10px] text-slate-500 uppercase">From</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-primary-600">India</div>
                  <div className="text-[10px] text-slate-500 uppercase">To</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-800">Apr {profile.targetReturnYear}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Return Date</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-slate-800">{formatUSD(usAssetsTotal + (indiaAssetsTotal / 91.8))}</div>
                  <div className="text-[10px] text-slate-500 uppercase">Net Worth</div>
                </div>
              </div>

              {/* Detailed Sections */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Profile Inputs */}
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <SectionHeader icon="👤" title="Your Profile" />
                  <div className="space-y-0">
                    <InputRow label="Current Age" value={profile.age} source="user" />
                    <InputRow label="Retirement Age" value={profile.retirementAge} source="user" />
                    <InputRow label="Return Year" value={profile.targetReturnYear} source="user" highlight />
                    <InputRow label="Marital Status" value={profile.maritalStatus === 'married' ? 'Married' : 'Single'} source="user" />
                    {profile.hasChildren && (profile.numberOfChildren ?? 0) > 0 && (
                      <InputRow label="Children" value={`${profile.numberOfChildren ?? 0}${profile.childrenAges?.length ? ` (ages: ${profile.childrenAges.join(', ')})` : ''}`} source="user" />
                    )}
                  </div>
                </div>

                {/* RNOR Eligibility */}
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <SectionHeader icon="🛡️" title="RNOR Eligibility" />
                  <div className="space-y-0">
                    <InputRow
                      label="Eligible"
                      value={taxStrategy.rnpioBased.eligible ? 'Yes ✓' : 'No'}
                      source="calculated"
                      highlight
                    />
                    <InputRow
                      label="RNOR Years"
                      value={taxStrategy.rnpioBased.rnorYears}
                      source="calculated"
                    />
                    {taxStrategy.rnpioBased.rnorStartDate && (
                      <InputRow
                        label="RNOR Window"
                        value={`${taxStrategy.rnpioBased.rnorStartDate} - ${taxStrategy.rnpioBased.rnorEndDate}`}
                        source="calculated"
                      />
                    )}
                    <div className="mt-2 p-2 bg-slate-50 rounded-lg">
                      <p className="text-[11px] text-slate-500 leading-relaxed">
                        <span className="font-medium text-slate-600">Why: </span>
                        {taxStrategy.rnpioBased.eligibilityReason}
                      </p>
                    </div>
                  </div>
                </div>

                {/* US Assets */}
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <SectionHeader icon="🇺🇸" title={`${profile.currentCountry} Assets`} />
                  <div className="space-y-0">
                    <InputRow
                      label="Total"
                      value={formatUSD(usAssetsTotal)}
                      source="user"
                      highlight
                    />
                    {assets.residentCountry.breakdown.retirement > 0 && (
                      <InputRow
                        label="Retirement (401k/IRA/HSA)"
                        value={formatUSD(assets.residentCountry.breakdown.retirement)}
                        source="user"
                      />
                    )}
                    {assets.residentCountry.breakdown.rsusOptions > 0 && (
                      <InputRow
                        label="RSUs/ESPP/Options"
                        value={formatUSD(assets.residentCountry.breakdown.rsusOptions)}
                        source="user"
                      />
                    )}
                    {assets.residentCountry.breakdown.taxableInvestments > 0 && (
                      <InputRow
                        label="Taxable Investments"
                        value={formatUSD(assets.residentCountry.breakdown.taxableInvestments)}
                        source="user"
                      />
                    )}
                    {assets.residentCountry.breakdown.liquid > 0 && (
                      <InputRow
                        label="Cash/Savings"
                        value={formatUSD(assets.residentCountry.breakdown.liquid)}
                        source="user"
                      />
                    )}
                  </div>
                </div>

                {/* India Assets */}
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <SectionHeader icon="🇮🇳" title="India Assets" />
                  <div className="space-y-0">
                    <InputRow
                      label="Total"
                      value={formatINR(indiaAssetsTotal)}
                      source="user"
                      highlight
                    />
                    {assets.india.breakdown.nps > 0 && (
                      <InputRow label="NPS" value={formatINR(assets.india.breakdown.nps)} source="user" />
                    )}
                    {assets.india.breakdown.epfPpf > 0 && (
                      <InputRow label="EPF/PPF" value={formatINR(assets.india.breakdown.epfPpf)} source="user" />
                    )}
                    {assets.india.breakdown.mutualFunds > 0 && (
                      <InputRow label="Mutual Funds" value={formatINR(assets.india.breakdown.mutualFunds)} source="user" />
                    )}
                    {assets.india.breakdown.realEstate > 0 && (
                      <InputRow label="Real Estate" value={formatINR(assets.india.breakdown.realEstate)} source="user" />
                    )}
                  </div>
                </div>

                {/* FIRE Metrics */}
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <SectionHeader icon="🔥" title="FIRE Metrics" />
                  <div className="space-y-0">
                    <InputRow
                      label="FIRE Number"
                      value={formatINR(fireSnapshot.fireNumber)}
                      source="calculated"
                      highlight
                    />
                    <InputRow
                      label="Current Net Worth"
                      value={formatINR(fireSnapshot.currentNetWorth)}
                      source="calculated"
                    />
                    <InputRow
                      label="Progress"
                      value={`${fireSnapshot.progressPercent.toFixed(0)}%`}
                      source="calculated"
                    />
                    <InputRow
                      label="Annual Expenses (FIRE)"
                      value={formatINR(fireSnapshot.annualExpensesAtFIRE_INR)}
                      source="user"
                    />
                  </div>
                </div>

                {/* Key Assumptions */}
                <div className="bg-white rounded-xl p-4 border border-slate-200">
                  <SectionHeader icon="⚙️" title="Assumptions Used" />
                  <div className="space-y-0">
                    <InputRow
                      label="Exchange Rate"
                      value={`₹${(indiaAssetsTotal / (usAssetsTotal || 1)).toFixed(1) || '91.8'} / $1`}
                      source="default"
                    />
                    <InputRow
                      label="Safe Withdrawal Rate"
                      value={`${(fireSnapshot.safeWithdrawalRate * 100).toFixed(1)}%`}
                      source="user"
                    />
                    {dataQuality.assumptions.slice(0, 3).map((assumption, idx) => (
                      <InputRow
                        key={idx}
                        label={assumption.category}
                        value={assumption.assumption.replace(/^[^:]+:\s*/, '')}
                        source="default"
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Data Quality Notice */}
              {dataQuality.missingFields.length > 0 && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <span className="text-lg">⚠️</span>
                    <div>
                      <h4 className="text-sm font-semibold text-amber-800 mb-1">Data Gaps</h4>
                      <p className="text-xs text-amber-700 mb-2">
                        Some calculations used defaults due to missing inputs:
                      </p>
                      <ul className="space-y-1">
                        {dataQuality.missingFields.slice(0, 3).map((field, idx) => (
                          <li key={idx} className="text-xs text-amber-600 flex items-start gap-1.5">
                            <span>•</span>
                            <span>
                              <span className="font-medium">{field.field}</span>
                              {field.assumption && <span className="text-amber-500"> - {field.assumption}</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div className="flex items-center justify-center gap-2 text-xs text-slate-400 pt-2">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Generated on {new Date(factPack.generatedAt).toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default InputSummary;
